import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Prompt } from 'react-router-dom';
import { Button, Icon, Message } from 'semantic-ui-react';
import Notifications from 'react-notification-system-redux';

import RTKLoader from '@/components/RTKLoader';
import useTranslate from '@/hooks/useTranslate';
import {
    useDeleteArticleMappingMutation,
    useGetArticleMappingsQuery,
    useGetArticlesQuery,
    useUpdateArticleMappingsMutation,
} from '@/services/bmApi';
import { ArticleMapping, ArticleMappingToHandle, MappableArticle } from '@/types';
import ArticleMappingsTable from './ArticleMappingsTable';

const REFERENCE_TYPE = 'EAccountingArticle';

type PendingChanges = Record<number, string | null>;

const ArticleMappingsTab: React.FC = () => {
    const { translate } = useTranslate();
    const dispatch = useDispatch();

    const [pending, setPending] = useState<PendingChanges>({});

    const articles = useGetArticlesQuery({ Active: true });
    const mappings = useGetArticleMappingsQuery({ ReferenceType: REFERENCE_TYPE });

    const [updateArticleMappings, updateState] = useUpdateArticleMappingsMutation();
    const [deleteArticleMapping, deleteState] = useDeleteArticleMappingMutation();

    const changed = Object.keys(pending).length > 0;
    const saving = updateState.isLoading || deleteState.isLoading;

    const mappableArticles = (articles.data?.Results ?? []).filter(
        (a): a is MappableArticle => typeof a.Id === 'number'
    );

    const savedMappingFor = (articleId: number): ArticleMapping | undefined =>
        mappings.data?.find((m) => m.ArticleId === articleId);

    const selectionFor = (article: MappableArticle) => {
        if (article.Id in pending) return pending[article.Id] ?? undefined;
        return savedMappingFor(article.Id)?.ExternalReference;
    };

    const onChange = (article: MappableArticle, externalReference: string | null) => {
        const saved = savedMappingFor(article.Id)?.ExternalReference ?? null;

        setPending((current) => {
            const next = { ...current };
            if (externalReference === saved) delete next[article.Id];
            else next[article.Id] = externalReference;
            return next;
        });
    };

    const onSave = async () => {
        const entries = Object.entries(pending).map(([articleId, externalReference]) => ({
            articleId: Number(articleId),
            externalReference,
        }));

        const toUpsert: ArticleMappingToHandle[] = entries
            .filter((e) => e.externalReference)
            .map((e) => ({
                ArticleId: e.articleId,
                ExternalReference: e.externalReference as string,
                ReferenceType: REFERENCE_TYPE,
            }));

        const toDelete = entries
            .filter((e) => !e.externalReference)
            .map((e) => savedMappingFor(e.articleId)?.Id)
            .filter((id): id is string => Boolean(id));

        try {
            for (const id of toDelete) {
                await deleteArticleMapping(id).unwrap();
            }

            if (toUpsert.length > 0) {
                await updateArticleMappings({ ArticleMappings: toUpsert }).unwrap();
            }

            setPending({});
            dispatch(
                Notifications.show(
                    { title: translate('form.submitSuccessMessage') as string },
                    'success'
                )
            );
        } catch (error) {
            dispatch(Notifications.show({ title: translate('common.error') as string }, 'error'));
        }
    };

    return (
        <>
            <Message icon info>
                <Icon name="info" />
                <span
                    dangerouslySetInnerHTML={{
                        __html: translate('eaccounting.articleMappings.messageText') as string,
                    }}
                />
            </Message>
            <RTKLoader queries={[articles, mappings]}>
                <ArticleMappingsTable
                    translate={translate}
                    articles={mappableArticles}
                    selectionFor={selectionFor}
                    onChange={onChange}
                    renderSaveButton={() => (
                        <div className="clearfix">
                            <Button
                                data-cy="submit-article-mapping"
                                onClick={onSave}
                                disabled={!changed || saving}
                                loading={saving}
                                floated="right"
                                primary
                            >
                                {translate('form.save')}
                            </Button>
                        </div>
                    )}
                />
            </RTKLoader>
            <Prompt
                when={changed}
                // @ts-ignore
                message={translate('form.unsavedChangesMessage')}
            />
        </>
    );
};

export default ArticleMappingsTab;
